import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from "react-router-dom";
import {
  Layout,
  Card,
  Avatar,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Tag,
  Divider,
  Space,
  ConfigProvider,
  Spin,
  message,
  Upload
} from 'antd';
import {
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  EditOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  LoadingOutlined,
  CameraOutlined,
  InstagramOutlined,
  FacebookOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import companyApi from '../../api/companyApi';

const { Title, Text, Link } = Typography;
const { Content } = Layout;

const CompanyDetails = () => {
  const navigate = useNavigate();
  const {id} = useParams();
  // console.log('Company ID from URL:', id);
  const brandColor = '#3C007A';
  const accentColor = '#FF8F00';
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  // Default fallback images
  const defaultLogo = 'https://ui-avatars.com/api/?name=Company&background=3C007A&color=fff&size=128&bold=true';
  const defaultBanner = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop';

  // Helper function to get image URL from server
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    const apiUrl = process.env.REACT_APP_API_URL;

    // If imagePath is already a full URL, return it
    if (imagePath.startsWith('http')) return imagePath;

    // Convert Windows backslashes to forward slashes
    let normalizedPath = imagePath.replace(/\\/g, '/');
    // Strip any leading slash so we can consistently prefix below
    normalizedPath = normalizedPath.replace(/^\/+/, '');

    // If path already includes the uploads/ segment, use it directly
    if (normalizedPath.startsWith('uploads/')) {
      return `${apiUrl}/${normalizedPath}`;
    }

    // Otherwise, assume it's just the filename
    return `${apiUrl}/uploads/${normalizedPath}`;
  };

  useEffect(() => {
    fetchCompanyProfile();
  }, [id]);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);

      // Get user info from localStorage
     
      // Fetch company profile using the company field from userInfo
      // userInfo.company contains the company _id: 693bb71cb2c43a05f5c610d8
      if (id) {
        const data = await companyApi.getCompanyProfile(id);
        // console.log('Company Data:', data);
        setCompanyData(data);
        // message.success('Profile loaded successfully');
      } else {
        message.error('No company ID found. Please login again.');
      }
    } catch (error) {
      message.error('Failed to load company profile');
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (file) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const formData = new FormData();
      formData.append('logo', file);

      // Use company ID for update
      await companyApi.updateCompanyProfile(userInfo.company, formData);
      message.success('Logo uploaded successfully');
      fetchCompanyProfile(); // Refresh profile data
      return false; // Prevent auto upload
    } catch (error) {
      message.error('Failed to upload logo');
      return false;
    }
  };

  const handleBannerUpload = async (file) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const formData = new FormData();
      formData.append('banner', file);

      // Use company ID for update
      await companyApi.updateCompanyProfile(userInfo.company, formData);
      message.success('Banner uploaded successfully');
      fetchCompanyProfile(); // Refresh profile data
      return false; // Prevent auto upload
    } catch (error) {
      message.error('Failed to upload banner');
      return false;
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'verified': 'success',
      'pending': 'warning',
      'active': 'success',
      'approved': 'success',
      'inactive': 'default',
      'rejected': 'error'
    };
    return statusMap[status?.toLowerCase()] || 'default';
  };

  const getStatusText = (status) => {
    const statusTextMap = {
      'approved': 'Verified',
      'pending': 'Pending',
      'rejected': 'Rejected',
      'active': 'Active',
      'verified': 'Verified',
      'inactive': 'Inactive'
    };
    return statusTextMap[status?.toLowerCase()] || status || 'Pending';
  };

  const handleEditProfile = () => {
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    fetchCompanyProfile(); // Refresh profile data after successful edit
  };

  // Get logo and banner URLs with error state
  const [bannerError, setBannerError] = useState(false);
  const logoUrl = companyData?.logo ? getImageUrl(companyData.logo) : defaultLogo;
  const bannerUrl = companyData?.banner ? getImageUrl(companyData.banner) : defaultBanner;

  // console.log('Logo URL:', logoUrl);
  // console.log('Banner URL:', bannerUrl);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: brandColor }} spin />} />
      </div>
    );
  }

  if (!companyData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f0f2f5' }}>
        <Text type="secondary">No company data available. Please contact support.</Text>
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: brandColor,
          fontFamily: "'Rubik', sans-serif",
          borderRadius: 6,
          fontSize: 16,
        },
      }}
    >
      <div style={{
        width: '100%',
        minHeight: '100%',
        padding:"30px",
        backgroundColor: '#f0f2f5',
        position: 'relative',
        overflowX: 'hidden',
        fontFamily: "'Rubik', sans-serif"
      }}>
        {/* Back Button */}
        <div style={{
          maxWidth: '100%',
          marginBottom: '20px',
          paddingLeft: '24px'
        }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{
              fontSize: '16px',
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 400,
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '1px solid #d9d9d9',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: brandColor,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = brandColor;
              e.currentTarget.style.color = brandColor;
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(60, 0, 122, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d9d9d9';
              e.currentTarget.style.color = brandColor;
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Back
          </Button>
        </div>

        {/* Banner Section */}
        <div style={{
          height: '290px',
          background: `url(${bannerError ? defaultBanner : bannerUrl}) center center / cover no-repeat`,
          position: 'relative',
          width: '100%',
          maxWidth: '100%'
        }}>
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(to bottom, rgba(60, 0, 122, 0.3), rgba(0,0,0,0.6))'
          }} />

          {/* Hidden image to check if banner loads */}
          <img
            src={bannerUrl}
            style={{ display: 'none' }}
            onError={() => {
              console.error('Banner failed to load:', bannerUrl);
              setBannerError(true);
            }}
            onLoad={() => console.log('Banner loaded successfully')}
            alt="banner"
          />

          {/* Upload Banner Button */}
          <Upload
            showUploadList={false}
            beforeUpload={handleBannerUpload}
            accept="image/*"
          >
            <Button
              icon={<CameraOutlined />}
              style={{ position: 'absolute', top: 20, right: 20, zIndex: 2 }}
            >
              Change Banner
            </Button>
          </Upload>
        </div>

        {/* Content Container */}
        <div style={{
          padding: '0 24px 24px',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Header Card */}
          <Card
            bordered={false}
            style={{
              marginTop: -60,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '100%'
            }}
            bodyStyle={{ padding: '24px 32px' }}
          >
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} sm={6} md={4} style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  // border: '4px solid white',
                  // boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  marginTop: -30,
                  // backgroundColor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {logoError || !companyData?.logo ? (
                    <Avatar
                      size={130}
                      style={{
                        backgroundColor: brandColor,
                        fontSize: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}
                      icon={<UserOutlined />}
                    >
                      {(companyData?.companyName || companyData?.name)?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  ) : (
                    <img
                      src={logoUrl}
                      alt="Company Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        padding: '8px'
                      }}
                      onError={() => {
                        console.error('Logo failed to load:', logoUrl);
                        setLogoError(true);
                      }}
                      onLoad={() => console.log('Logo loaded successfully')}
                    />
                  )}
                </div>

                <Upload showUploadList={false} beforeUpload={handleLogoUpload} accept="image/*">
                  <Button
                    icon={<CameraOutlined />}
                    shape="circle"
                    style={{
                      position: 'absolute',
                      bottom: 5,
                      right: '50%',
                      transform: 'translateX(58px)',
                      zIndex: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  />
                </Upload>
              </Col>

              <Col xs={24} sm={18} md={14}>
                <Space direction="vertical" size={8}>
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      color: brandColor,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 500,
                      fontSize: '32px'
                    }}
                  >
                    {companyData.companyName || companyData.name || 'Company Name'}
                  </Title>
                  <Space wrap>
                    <Tag
                      color="purple"
                      style={{
                        margin: 0,
                        fontSize: '16px',
                        padding: '4px 12px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400
                      }}
                    >
                      {companyData.industry || companyData.industryType || companyData.sector || 'Not Specified'}
                    </Tag>
                    <Tag
                      icon={<SafetyCertificateOutlined />}
                      color={getStatusColor(companyData.status || companyData.accountStatus)}
                      style={{
                        fontSize: '16px',
                        padding: '4px 12px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400
                      }}
                    >
                      {getStatusText(companyData.status || companyData.accountStatus)}
                    </Tag>
                  </Space>
                  {companyData.website && (
                    <Link
                      href={companyData.website}
                      target="_blank"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginLeft: 52,
                        fontSize: '16px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400,
                        color: "#333"
                      }}
                    >
                      <GlobalOutlined /> {companyData.website}
                    </Link>
                  )}

                  {/* Social Networks */}
                  {(companyData.socialNetworks?.instagram || companyData.socialNetworks?.facebook ||
                    companyData.socialNetworks?.linkedin || companyData.socialNetworks?.twitter) && (
                      <Space size="large" style={{ marginTop: 8 }}>
                        {companyData.socialNetworks?.instagram && (
                          <Link href={companyData.socialNetworks.instagram} target="_blank">
                            <InstagramOutlined style={{ fontSize: 24, color: '#E4405F' }} />
                          </Link>
                        )}
                        {companyData.socialNetworks?.facebook && (
                          <Link href={companyData.socialNetworks.facebook} target="_blank">
                            <FacebookOutlined style={{ fontSize: 24, color: '#1877F2' }} />
                          </Link>
                        )}
                        {companyData.socialNetworks?.linkedin && (
                          <Link href={companyData.socialNetworks.linkedin} target="_blank">
                            <LinkedinOutlined style={{ fontSize: 24, color: '#0A66C2' }} />
                          </Link>
                        )}
                        {companyData.socialNetworks?.twitter && (
                          <Link href={companyData.socialNetworks.twitter} target="_blank">
                            <TwitterOutlined style={{ fontSize: 24, color: '#1DA1F2' }} />
                          </Link>
                        )}
                      </Space>
                    )}
                </Space>
              </Col>

              <Col xs={24} md={6} style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                {/* <Button
                  icon={<MailOutlined />}
                  onClick={() => navigate('/contactusform')}
                  style={{
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    height: '40px'
                  }}
                >
                  Message
                </Button> */}
               
              </Col>
            </Row>
          </Card>

          {/* Details Grid */}
          <Row gutter={[24, 24]} style={{ marginTop: 24, width: '100%', maxWidth: '100%' }}>

            {/* Left Column: Organization Details */}
            <Col xs={24} lg={16}>
              <Card
                title={
                  <Space style={{ fontSize: '20px', fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                    <BankOutlined style={{ color: brandColor, fontSize: '20px' }} />
                    Organization Details
                  </Space>
                }
                bordered={false}
                style={{ height: '100%' }}
              >
                <Descriptions
                  column={{ xs: 1, sm: 2 }}
                  layout="vertical"
                  labelStyle={{
                    fontWeight: 400,
                    color: '#666',
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif"
                  }}
                  contentStyle={{
                    fontSize: '17px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}
                >
                  <Descriptions.Item label="Company Name">
                    <Text strong style={{ fontSize: 18, fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                      {companyData.companyName || companyData.name || 'N/A'}
                    </Text>
                  </Descriptions.Item>

                  <Descriptions.Item label="Director / CEO Name">
                    <Space>
                      {/* <Avatar icon={<UserOutlined />} style={{ backgroundColor: accentColor }} size="default" /> */}
                      <Text strong style={{ fontSize: 17, fontFamily: "'Rubik', sans-serif", fontWeight: 400 }}>
                        {companyData.directorCeo || 'N/A'}
                      </Text>
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Industry Sector">
                    {companyData.industry || companyData.industryType || companyData.sector || 'Not Specified'}
                  </Descriptions.Item>

                  <Descriptions.Item label="Registration ID">
                    <Text
                      copyable
                      style={{
                        fontSize: 15,
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400,
                        color: "#333",
                        letterSpacing: '0.5px'
                      }}
                    >
                      {companyData.registrationId || `#AID${companyData._id?.slice(-4).toUpperCase()}` || 'N/A'}
                    </Text>
                  </Descriptions.Item>

                  {companyData.gstin && (
                    <Descriptions.Item label="GSTIN">
                      <Text copyable>{companyData.gstin}</Text>
                    </Descriptions.Item>
                  )}

                  {companyData.yearEstablished && (
                    <Descriptions.Item label="Year Established">
                      {companyData.yearEstablished}
                    </Descriptions.Item>
                  )}

                  {companyData.numberOfEmployees && (
                    <Descriptions.Item label="Number of Employees">
                      {companyData.numberOfEmployees}
                    </Descriptions.Item>
                  )}

                  {companyData.annualTurnover && (
                    <Descriptions.Item label="Annual Turnover">
                      ₹ {companyData.annualTurnover}
                    </Descriptions.Item>
                  )}
                </Descriptions>

                <Divider />

                <Title
                  level={5}
                  style={{
                    marginBottom: 20,
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    fontSize: '20px',
                    color: brandColor
                  }}
                >
                  About
                </Title>
                <Text
                  type="secondary"
                  style={{
                    lineHeight: 1.8,
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: '#333'
                  }}
                >
                  {companyData.description || companyData.aboutCompany || companyData.about ||
                    `${companyData.companyName || companyData.name || 'Our company'} is a leading player in the ${companyData.industry || companyData.industryType || 'business'} sector. 
                  We are committed to delivering excellence and innovation.`}
                </Text>
              </Card>
            </Col>

            {/* Right Column: Contact Information */}
            {/* Right Column: Contact Information */}
            <Col xs={20} lg={6}>
              <Card
                title={
                  <Space style={{
                    fontSize: '20px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: '#3C007A' // Darker title color like screenshot
                  }}>
                    <EnvironmentOutlined style={{ color: brandColor }} /> {/* Purple Icon in header */}
                    Contact Information
                  </Space>
                }
                bordered={false}
                style={{
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
                headStyle={{
                  borderBottom: 'none', // Removed heavy border like screenshot
                  padding: '12px 12px 0 12px'
                }}
                bodyStyle={{
                  padding: '24px'
                }}
              >

                <Space direction="vertical" size={24} style={{ width: '90%' }}>
                  <Divider style={{ margin: '3px 0' }} />
                  {/* Contact Person */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5', // Light gray background
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <UserOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>
                        Contact Person</Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                        {companyData.contactName || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <MailOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>Email Address</Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                        {companyData.email || companyData.contactEmail || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  {/* Phone */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PhoneOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>Mobile Number</Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                        {companyData.phone || companyData.contactNumber || companyData.mobileNumber || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  <Divider style={{ margin: '0px 0' }} />

                  {/* Registered Address - Centered Layout */}
                  <div style={{ textAlign: 'center', marginTop: 0 }}>
                    <Text type="secondary" style={{ fontSize: 18, marginBottom: 8, display: 'block' }}>
                      Registered Address
                    </Text>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'flex-start' }}>
                      <EnvironmentOutlined style={{ color: brandColor, marginTop: 4 }} />
                      <Text style={{
                        fontSize: 14,
                        color: '#333',
                        maxWidth: '80%',
                        lineHeight: 1.5
                      }}>
                        {companyData.address?.street ? `${companyData.address.street}, ` : ''}
                        {companyData.address?.city}
                        {(companyData.address?.state) ? `, ${companyData.address.state}` : ''}
                        <br />
                        {companyData.address?.country || 'India'} - {companyData.address?.pincode || ''}
                      </Text>
                    </div>
                  </div>

                </Space>
              </Card>
            </Col>

          </Row>
        </div>
      </div>

    
    </ConfigProvider>
  );
};

export default CompanyDetails;